import React, { useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Sparkles, Download, Filter, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  CalendarDays, Wallet, Activity, CheckCircle, AlertCircle, FileText, UserCheck,
  MapPin, Trophy, BarChart3, FileDown, FileSpreadsheet, Landmark, Banknote, Clock,
} from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import { downloadFile } from '../ui-pro/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KASA_META = {
  nakit: { label: 'Nakit', icon: '💵', accent: 'from-emerald-500 to-green-600' },
  garanti_bbva: { label: 'Garanti BBVA', icon: '🏦', accent: 'from-red-600 to-rose-700' },
  bostanci: { label: 'Bostancı', icon: '🏢', accent: 'from-amber-600 to-orange-700' },
};

const formatTL = (v) => `₺${(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;

export const computeAnalytics = ({ reservations, incomes, expenses, getSalonName, analyticsFilters }) => {
  const { date_from, date_to } = analyticsFilters;
  const filteredReservations = reservations.filter(r => {
    if (date_from && r.date < date_from) return false;
    if (date_to && r.date > date_to) return false;
    return true;
  });
  const filteredIncomes = incomes.filter(i => {
    if (date_from && i.date < date_from) return false;
    if (date_to && i.date > date_to) return false;
    return true;
  });
  const filteredExpenses = expenses.filter(e => {
    if (date_from && e.date < date_from) return false;
    if (date_to && e.date > date_to) return false;
    return true;
  });

  const totalIncome = filteredIncomes.reduce((s, i) => s + (i.amount || 0), 0);
  const reservationIncome = filteredIncomes.filter(i => i.category === 'rezervasyon').reduce((s, i) => s + (i.amount || 0), 0);
  const matchIncome = filteredIncomes.filter(i => i.category === 'mac').reduce((s, i) => s + (i.amount || 0), 0);
  const otherIncome = filteredIncomes.filter(i => !['rezervasyon', 'mac'].includes(i.category)).reduce((s, i) => s + (i.amount || 0), 0);

  const totalExpense = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const expenseByCategory = {};
  filteredExpenses.forEach(e => {
    const cat = e.category || 'diger';
    expenseByCategory[cat] = (expenseByCategory[cat] || 0) + (e.amount || 0);
  });

  const totalReservations = filteredReservations.length;
  const paidReservations = filteredReservations.filter(r => r.payment_status === 'odendi').length;
  const unpaidReservations = filteredReservations.filter(r => r.payment_status === 'odenmedi').length;
  const invoicedReservations = filteredReservations.filter(r => r.invoice_issued).length;
  const reservationRevenue = filteredReservations.reduce((s, r) => s + (r.total_amount || 0), 0);

  const revenueBySalon = {};
  filteredReservations.forEach(r => {
    const salonName = getSalonName(r.salon_id);
    revenueBySalon[salonName] = (revenueBySalon[salonName] || 0) + (r.total_amount || 0);
  });

  const refereeReservations = filteredReservations.filter(r => r.referee_id && r.referee_fee);
  const totalRefereeFees = refereeReservations.reduce((s, r) => s + (r.referee_fee || 0), 0);

  const kasaBreakdown = {};
  ['nakit', 'garanti_bbva', 'bostanci'].forEach(pm => {
    kasaBreakdown[pm] = { income: 0, expense: 0, net: 0 };
  });
  filteredIncomes.forEach(i => {
    const pm = i.payment_method || 'nakit';
    if (!kasaBreakdown[pm]) kasaBreakdown[pm] = { income: 0, expense: 0, net: 0 };
    kasaBreakdown[pm].income += (i.amount || 0);
  });
  filteredExpenses.forEach(e => {
    const pm = e.payment_method || 'nakit';
    if (!kasaBreakdown[pm]) kasaBreakdown[pm] = { income: 0, expense: 0, net: 0 };
    kasaBreakdown[pm].expense += (e.amount || 0);
  });
  Object.keys(kasaBreakdown).forEach(k => {
    kasaBreakdown[k].net = kasaBreakdown[k].income - kasaBreakdown[k].expense;
  });

  const customerRevenue = {};
  filteredReservations.forEach(r => {
    if (r.customer_id) {
      customerRevenue[r.customer_id] = (customerRevenue[r.customer_id] || 0) + (r.total_amount || 0);
    }
  });

  const profitMargin = totalIncome > 0 ? ((totalIncome - totalExpense) / totalIncome) * 100 : 0;
  const paidRatio = totalReservations > 0 ? (paidReservations / totalReservations) * 100 : 0;
  const avgReservationValue = totalReservations > 0 ? reservationRevenue / totalReservations : 0;

  const daysDiff = Math.max(1, Math.ceil((new Date(date_to) - new Date(date_from)) / (1000 * 60 * 60 * 24)) + 1);
  const dailyAvgIncome = totalIncome / daysDiff;
  const dailyAvgReservations = totalReservations / daysDiff;

  return {
    totalIncome, reservationIncome, matchIncome, otherIncome,
    totalExpense, expenseByCategory,
    netProfit: totalIncome - totalExpense, profitMargin,
    totalReservations, paidReservations, unpaidReservations, invoicedReservations,
    paidRatio, avgReservationValue, reservationRevenue, revenueBySalon,
    totalRefereeFees, refereeReservationCount: refereeReservations.length,
    kasaBreakdown, customerRevenue,
    dailyAvgIncome, dailyAvgReservations, daysDiff,
  };
};

const ReportsTab = ({
  reservations = [],
  incomes = [],
  expenses = [],
  getSalonName,
  getCustomerName,
  refereesTotalBalance,
  analyticsFilters,
  setAnalyticsFilters,
  exportAnalyticsToExcel,
}) => {
  const a = useMemo(
    () => computeAnalytics({ reservations, incomes, expenses, getSalonName, analyticsFilters }),
    [reservations, incomes, expenses, getSalonName, analyticsFilters]
  );

  const [summary, setSummary] = useState(null);
  const [group, setGroup] = useState('month');
  const [exporting, setExporting] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!analyticsFilters.date_from || !analyticsFilters.date_to) return;
    try {
      const res = await axios.get(`${API}/reports/financial/summary`, {
        params: { start: analyticsFilters.date_from, end: analyticsFilters.date_to, group },
      });
      setSummary(res.data);
    } catch (e) { setSummary(null); }
  }, [analyticsFilters.date_from, analyticsFilters.date_to, group]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const exportPeriod = async (format) => {
    setExporting(format);
    await downloadFile(`${API}/reports/financial/export`, {
      format, start: analyticsFilters.date_from, end: analyticsFilters.date_to, group,
    }, `finansal_rapor_${analyticsFilters.date_from}_${analyticsFilters.date_to}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
    setExporting(null);
  };

  const PERIODS = [
    { id: 'day', label: 'Bugün', icon: CalendarDays, apply: () => { const t = new Date().toISOString().split('T')[0]; setAnalyticsFilters({ period: 'day', date_from: t, date_to: t }); } },
    { id: 'week', label: 'Son 7 Gün', icon: Clock, apply: () => { const t = new Date(); const w = new Date(t.getTime() - 6 * 86400000); setAnalyticsFilters({ period: 'week', date_from: w.toISOString().split('T')[0], date_to: t.toISOString().split('T')[0] }); } },
    { id: 'month', label: 'Bu Ay', icon: CalendarDays, apply: () => { const t = new Date(); const m = new Date(t.getFullYear(), t.getMonth(), 1); setAnalyticsFilters({ period: 'month', date_from: m.toISOString().split('T')[0], date_to: t.toISOString().split('T')[0] }); } },
    { id: 'quarter', label: 'Son 90 Gün', icon: BarChart3, apply: () => { const t = new Date(); const q = new Date(t.getTime() - 89 * 86400000); setAnalyticsFilters({ period: 'quarter', date_from: q.toISOString().split('T')[0], date_to: t.toISOString().split('T')[0] }); } },
    { id: 'year', label: 'Bu Yıl', icon: Trophy, apply: () => { const t = new Date(); const y = new Date(t.getFullYear(), 0, 1); setAnalyticsFilters({ period: 'year', date_from: y.toISOString().split('T')[0], date_to: t.toISOString().split('T')[0] }); } },
  ];

  const topCustomers = Object.entries(a.customerRevenue || {})
    .sort((x, y) => y[1] - x[1])
    .slice(0, 5)
    .map(([cid, amt]) => ({ name: getCustomerName(cid), amount: amt }));

  const formatRange = () => {
    if (!analyticsFilters.date_from) return '';
    const from = new Date(analyticsFilters.date_from).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
    const to = new Date(analyticsFilters.date_to).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${from} → ${to}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Finansal Raporlar"
        description={`${formatRange()} · ${a.daysDiff} gün · ${a.totalReservations} rezervasyon`}
        testid="reports-page-header"
        actions={
          <>
            <div className={`px-3 py-1.5 rounded-lg border ${a.netProfit >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Kâr Marjı</p>
              <p className={`text-base font-bold ${a.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                %{a.profitMargin.toFixed(1)}
              </p>
            </div>
            <Button variant="outline" className="h-9" disabled={exporting === 'pdf'}
              onClick={() => exportPeriod('pdf')} data-testid="reports-export-pdf">
              <FileDown className="w-4 h-4 mr-1" /> {exporting === 'pdf' ? 'Hazırlanıyor...' : 'PDF'}
            </Button>
            <Button className="h-9 bg-[#15803D] hover:bg-[#126c34]" disabled={exporting === 'excel'}
              onClick={() => exportPeriod('excel')} data-testid="reports-export-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> {exporting === 'excel' ? 'Hazırlanıyor...' : 'Excel'}
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-2" data-testid="reports-period-filter">
          <div className="flex items-center gap-1.5 flex-wrap">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={p.apply}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors border ${
                  analyticsFilters.period === p.id
                    ? 'bg-[#0B172A] text-white border-[#0B172A]'
                    : 'bg-white text-slate-700 border-[#E2E8F0] hover:bg-[#F8FAFC]'
                }`}
                data-testid={`reports-period-${p.id}`}
              >
                <p.icon className="w-3.5 h-3.5" /> {p.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[11px] text-slate-500">Başlangıç</Label>
              <Input type="date" value={analyticsFilters.date_from}
                onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, period: 'custom', date_from: e.target.value })}
                className="h-9 w-[146px]" data-testid="reports-date-from" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Bitiş</Label>
              <Input type="date" value={analyticsFilters.date_to}
                onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, period: 'custom', date_to: e.target.value })}
                className="h-9 w-[146px]" data-testid="reports-date-to" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Kırılım</Label>
              <select value={group} onChange={(e) => setGroup(e.target.value)}
                className="h-9 rounded-md border border-[#E2E8F0] bg-white text-[13px] px-2 text-slate-700"
                data-testid="reports-group">
                <option value="day">Günlük</option>
                <option value="month">Aylık</option>
                <option value="year">Yıllık</option>
              </select>
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={ArrowUpRight} tone="emerald" testid="kpi-income" label="Toplam Gelir"
          value={formatTL(a.totalIncome)} sub={`Günlük ort. ${formatTL(a.dailyAvgIncome)}`} />
        <StatCard icon={ArrowDownRight} tone="rose" testid="kpi-expense" label="Toplam Gider"
          value={formatTL(a.totalExpense)} sub={`${Object.keys(a.expenseByCategory).length} kategori`} />
        <StatCard icon={a.netProfit >= 0 ? TrendingUp : TrendingDown} tone={a.netProfit >= 0 ? 'sky' : 'amber'}
          testid="kpi-net" label="Net Kâr / Zarar" value={formatTL(a.netProfit)}
          sub={a.netProfit >= 0 ? 'Kârlı dönem' : 'Zararlı dönem'} />
        <StatCard icon={CalendarDays} tone="indigo" testid="kpi-reservations" label="Rezervasyon"
          value={a.totalReservations} sub={`Ort. ${formatTL(a.avgReservationValue)} / adet`} />
      </div>

      {/* DÖNEMSEL GRAFİKLER (sunucu taraflı özet) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-3" data-testid="reports-period-chart">
          <p className="text-[13px] font-semibold text-slate-800 mb-2">Dönemsel Gelir / Gider</p>
          {!summary ? <p className="text-xs text-slate-400 py-20 text-center">Yükleniyor...</p>
            : summary.periods.length === 0 ? <p className="text-xs text-slate-400 py-20 text-center">Seçili aralıkta veri yok</p> : (
              <ResponsiveContainer width="100%" height={236}>
                <BarChart data={summary.periods} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                  <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#E2E8F0' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" name="Gelir" fill="#15803D" radius={[3, 3, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="expense" name="Gider" fill="#DC2626" radius={[3, 3, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </div>
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-3" data-testid="reports-net-chart">
          <p className="text-[13px] font-semibold text-slate-800 mb-2">Net Kârlılık Trendi</p>
          {!summary ? <p className="text-xs text-slate-400 py-20 text-center">Yükleniyor...</p>
            : summary.periods.length === 0 ? <p className="text-xs text-slate-400 py-20 text-center">Seçili aralıkta veri yok</p> : (
              <ResponsiveContainer width="100%" height={236}>
                <LineChart data={summary.periods} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                  <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#E2E8F0' }} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#176B91" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
        </div>
      </div>

      {/* KASA BAZLI BREAKDOWN */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 inline-flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#176B91]" /> Kasa Bazlı Hareket
          </h3>
          <p className="text-xs text-slate-400">Dönem içi net</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(a.kasaBreakdown).map(([pm, vals]) => {
            const meta = KASA_META[pm] || { label: pm, icon: '💼', accent: 'from-slate-500 to-slate-700' };
            return (
              <div key={pm} className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-3.5" data-testid={`reports-kasa-${pm}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`rounded-lg p-1.5 ${pm === 'nakit' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-[#176B91]'}`}>
                      {pm === 'nakit' ? <Banknote className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
                    </div>
                    <p className="text-[12px] font-semibold text-slate-700 truncate">{meta.label}</p>
                  </div>
                  <p className={`text-[15px] font-bold ${vals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatTL(vals.net)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="bg-[#F8FAFC] rounded-lg p-2 border border-[#E2E8F0]">
                    <p className="text-slate-500">Giriş</p>
                    <p className="font-bold text-emerald-700 text-[13px]">{formatTL(vals.income)}</p>
                  </div>
                  <div className="bg-[#F8FAFC] rounded-lg p-2 border border-[#E2E8F0]">
                    <p className="text-slate-500">Çıkış</p>
                    <p className="font-bold text-rose-700 text-[13px]">{formatTL(vals.expense)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* GELİR / GİDER DETAYI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden" data-testid="reports-income-breakdown">
          <div className="bg-white border-b border-[#E2E8F0] text-emerald-800 px-3.5 py-2.5 flex items-center justify-between">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" /> Gelir Dağılımı
            </h4>
            <span className="text-[11px] text-slate-500">Kaynak bazlı</span>
          </div>
          <div className="p-4 space-y-2">
            {[
              { label: 'Rezervasyon Geliri', value: a.reservationIncome, color: 'bg-blue-500' },
              { label: 'Maç Geliri', value: a.matchIncome, color: 'bg-emerald-500' },
              { label: 'Diğer Gelirler', value: a.otherIncome, color: 'bg-purple-500' },
            ].map(row => {
              const pct = a.totalIncome > 0 ? (row.value / a.totalIncome) * 100 : 0;
              return (
                <div key={row.label} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 font-medium">{row.label}</span>
                    <span className="font-bold tabular-nums">{formatTL(row.value)} <span className="text-[10px] text-slate-400 font-normal">%{pct.toFixed(1)}</span></span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${row.color} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="font-bold text-slate-800">Toplam Gelir</span>
              <span className="font-black text-xl text-emerald-700 tabular-nums">{formatTL(a.totalIncome)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden" data-testid="reports-expense-breakdown">
          <div className="bg-white border-b border-[#E2E8F0] text-rose-800 px-3.5 py-2.5 flex items-center justify-between">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <ArrowDownRight className="w-4 h-4" /> Gider Kategorileri
            </h4>
            <span className="text-[11px] text-slate-500">Top 6</span>
          </div>
          <div className="p-4 space-y-2">
            {Object.entries(a.expenseByCategory)
              .sort((x, y) => y[1] - x[1])
              .slice(0, 6)
              .map(([cat, amt]) => {
                const pct = a.totalExpense > 0 ? (amt / a.totalExpense) * 100 : 0;
                const label = cat.replace(/_/g, ' ');
                return (
                  <div key={cat} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 font-medium capitalize">{label}</span>
                      <span className="font-bold tabular-nums">{formatTL(amt)} <span className="text-[10px] text-slate-400 font-normal">%{pct.toFixed(1)}</span></span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            {Object.keys(a.expenseByCategory).length === 0 && (
              <p className="text-center text-slate-400 py-6 text-sm">Bu dönemde gider yok</p>
            )}
            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="font-bold text-slate-800">Toplam Gider</span>
              <span className="font-black text-xl text-rose-700 tabular-nums">{formatTL(a.totalExpense)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* REZERVASYON & SALON */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden lg:col-span-1" data-testid="reports-reservation-funnel">
          <div className="bg-white border-b border-[#E2E8F0] text-indigo-800 px-3.5 py-2.5">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <Activity className="w-4 h-4" /> Rezervasyon Akışı
            </h4>
          </div>
          <div className="p-4 space-y-2.5">
            {[
              { label: 'Toplam', value: a.totalReservations, icon: CalendarDays, color: 'text-slate-700', bg: 'bg-slate-50' },
              { label: 'Ödenen', value: a.paidReservations, icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50' },
              { label: 'Ödenmeyen', value: a.unpaidReservations, icon: AlertCircle, color: 'text-amber-700', bg: 'bg-amber-50' },
              { label: 'Fatura Kesilen', value: a.invoicedReservations, icon: FileText, color: 'text-blue-700', bg: 'bg-blue-50' },
              { label: 'Hakem Atanan', value: a.refereeReservationCount, icon: UserCheck, color: 'text-orange-700', bg: 'bg-orange-50' },
            ].map(row => (
              <div key={row.label} className={`flex items-center justify-between p-2.5 rounded-lg ${row.bg}`}>
                <span className={`text-sm font-medium ${row.color} inline-flex items-center gap-2`}>
                  <row.icon className="w-3.5 h-3.5" /> {row.label}
                </span>
                <span className={`font-bold ${row.color} tabular-nums`}>{row.value}</span>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-sm">
              <span className="text-slate-600">Ödeme Oranı</span>
              <span className="font-black text-emerald-700">%{a.paidRatio.toFixed(0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Toplam Ciro</span>
              <span className="font-black text-purple-700 tabular-nums">{formatTL(a.reservationRevenue)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden lg:col-span-2" data-testid="reports-salon-revenue">
          <div className="bg-white border-b border-[#E2E8F0] text-[#176B91] px-3.5 py-2.5 flex items-center justify-between">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Salon Bazlı Ciro
            </h4>
            <span className="text-[11px] text-slate-500">{Object.keys(a.revenueBySalon).length} salon</span>
          </div>
          <div className="p-4">
            {Object.keys(a.revenueBySalon).length === 0 ? (
              <p className="text-center text-slate-400 py-12 text-sm">Bu dönemde rezervasyon yok</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(a.revenueBySalon)
                  .sort((x, y) => y[1] - x[1])
                  .map(([salon, amt], idx) => {
                    const pct = a.reservationRevenue > 0 ? (amt / a.reservationRevenue) * 100 : 0;
                    const colors = ['bg-indigo-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500'];
                    return (
                      <div key={salon}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2">
                            <span className="text-[10px] font-mono text-slate-400 w-5">#{String(idx + 1).padStart(2, '0')}</span>
                            {salon}
                          </span>
                          <span className="text-sm font-bold tabular-nums">{formatTL(amt)} <span className="text-[10px] text-slate-400 font-normal">%{pct.toFixed(1)}</span></span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${colors[idx % colors.length]} transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HAKEM & TOP MÜŞTERİLER */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden" data-testid="reports-referee-summary">
          <div className="bg-white border-b border-[#E2E8F0] text-amber-800 px-3.5 py-2.5">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <UserCheck className="w-4 h-4" /> Hakem Özeti
            </h4>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-orange-600 font-semibold">Hakemli Rez.</p>
              <p className="text-2xl font-black text-orange-700 mt-1">{a.refereeReservationCount}</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">Toplam Ücret</p>
              <p className="text-2xl font-black text-amber-700 mt-1 tabular-nums">{formatTL(a.totalRefereeFees)}</p>
            </div>
            <div className="text-center p-3 bg-rose-50 rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-rose-600 font-semibold">Alınacak</p>
              <p className="text-2xl font-black text-rose-700 mt-1 tabular-nums">{formatTL(refereesTotalBalance?.total_balance || 0)}</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Tahsil Edilen</p>
              <p className="text-2xl font-black text-emerald-700 mt-1 tabular-nums">{formatTL(refereesTotalBalance?.total_collected || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden" data-testid="reports-top-customers">
          <div className="bg-white border-b border-[#E2E8F0] text-slate-800 px-3.5 py-2.5 flex items-center justify-between">
            <h4 className="font-semibold text-[13px] flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" /> En İyi Müşteriler
            </h4>
            <span className="text-[11px] text-slate-500">Top 5</span>
          </div>
          <div className="p-4">
            {topCustomers.length === 0 ? (
              <p className="text-center text-slate-400 py-8 text-sm">Bu dönemde müşteri cirosu yok</p>
            ) : (
              <div className="space-y-2">
                {topCustomers.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-orange-600' : 'bg-slate-300'}`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                    </div>
                    <p className="font-bold text-slate-900 tabular-nums">{formatTL(c.amount)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsTab;
